import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Loader2, Send, Sparkles, User as UserIcon } from 'lucide-react';
import { tl } from '../../lib/testingLabApi';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

/**
 * Akro chat panel — Claude Sonnet 4.5 powered testing AI agent.
 * Scoped optionally to a test session and/or a finding.
 */
export const AkroChatPanel = ({ testSessionId = null, findingId = null, contextHint = null }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const params = {};
      if (testSessionId) params.test_session_id = testSessionId;
      if (findingId) params.finding_id = findingId;
      const data = await tl.akroMessages(params);
      setMessages(data.items || []);
    } catch (e) {
      toast.error(`Akro history failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMessages(); /* eslint-disable-line */ }, [testSessionId, findingId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    // Optimistic add
    setMessages((prev) => [...prev, { role: 'user', content: text, _temp: true }]);
    try {
      await tl.akroChat({
        message: text,
        test_session_id: testSessionId,
        finding_id: findingId,
        context: contextHint,
      });
      await loadMessages();
    } catch (e) {
      toast.error(`Akro: ${e.message}`);
      setMessages((prev) => prev.filter((m) => !m._temp));
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="flex flex-col h-full bg-card border border-border rounded-lg overflow-hidden" data-testid="akro-chat-panel">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-gradient-to-r from-violet-500/10 to-indigo-500/10">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold">Akro</div>
          <div className="text-[11px] text-muted-foreground">Testing intelligence · Claude Sonnet 4.5</div>
        </div>
        {(testSessionId || findingId) && (
          <Badge variant="outline" className="text-[10px]">
            {testSessionId ? `session ${testSessionId.slice(-6)}` : `finding ${findingId.slice(-6)}`}
          </Badge>
        )}
      </div>

      <ScrollArea className="flex-1" data-testid="akro-chat-scroll">
        <div ref={scrollRef} className="p-4 space-y-3 min-h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground text-xs">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> loading conversation
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-xs text-muted-foreground">
              Start a conversation with Akro. Try: <em>"What test should I run for the income ledger?"</em>
            </div>
          ) : (
            messages.map((m, i) => (
              <div
                key={m.message_id || i}
                className={cn(
                  'flex gap-2',
                  m.role === 'user' ? 'justify-end' : 'justify-start'
                )}
                data-testid={`akro-msg-${m.role}`}
              >
                {m.role === 'assistant' && (
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                )}
                <div
                  className={cn(
                    'max-w-[78%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words',
                    m.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  )}
                >
                  {m.content}
                </div>
                {m.role === 'user' && (
                  <div className="w-6 h-6 rounded-full bg-slate-300 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 mt-1">
                    <UserIcon className="w-3 h-3" />
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      <div className="border-t border-border p-3 bg-card">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="Ask Akro… (Cmd/Ctrl+Enter to send)"
            className="min-h-[60px] resize-none text-sm"
            disabled={sending}
            data-testid="akro-chat-input"
          />
          <Button
            onClick={send}
            disabled={!input.trim() || sending}
            size="sm"
            className="self-end"
            data-testid="akro-chat-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AkroChatPanel;
